import { useState, useCallback } from 'react';
import customAxios from '../util/Api';
import authHeader from 'services/auth/jwt/auth-header';

export const useRateCard = () => {
  const [rateCards, setRateCards] = useState([]);
  const [baseRates, setBaseRates] = useState([]);
  const [haulageCategories, setHaulageCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchParams = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customAxios.get('Ops/GetFuelParameters', { headers: authHeader() });
      // API returns an array — take the first element
      const first = Array.isArray(response?.data) ? response.data[0] : response?.data;
      return first ?? null;
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ── Rate card calculation ──────────────────────────────────────────────────

  const calculate = useCallback(async params => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await customAxios.post(`Ops/calculateNewFuelRates`, params, { headers: authHeader() });
      // Syncfusion requires a plain array
      setRateCards(Array.isArray(data) ? data : []);
      return data;
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateParams = useCallback(async params => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        id: 1,
        fuelCurrentRate: Number(params.fuelCurrentRate),
        fuelBaseRate: Number(params.fuelBaseRate),
        baseRatePercent: Number(params.baseRatePercent),
        subContractorRate: Math.round(Number(params.subContractorRate) * 100),
        tonnesPerLoad: Number(params.tonnesPerLoad),
        rateChangePercent: Number(params.rateChangePercent),
        haulageCategoryId: params.haulageCategory === 'standard' ? 0 : params.haulageCategory,
        priceChangePCT: Number(params.priceChangePCT),
        priceChangeAdj: Number(params.priceChangeAdj),
      };
      const { data } = await customAxios.post(`Ops/UpdateFuelParameters`, payload, { headers: authHeader() });
      // setRateCards(data);
      return data;
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Base rates ─────────────────────────────────────────────────────────────

  const fetchBaseRates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await customAxios.get(`Ops/GetBaseRate`, { headers: authHeader() });
      setBaseRates(data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch base rates');
    } finally {
      setLoading(false);
    }
  }, []);

  const addBaseRate = async payload => {
    try {
      await customAxios.post(`Ops/AddBaseRate`, { ...payload, insertedId: 0 }, { headers: authHeader() });
      await fetchBaseRates();
    } catch (err) {
      throw err;
    }
  };

  const updateBaseRate = async payload => {
    await customAxios.post(`Ops/UpdateBaseRate`, payload, { headers: authHeader() });
    await fetchBaseRates();
  };

  const deleteBaseRate = async id => {
    await customAxios.post(`/Ops/DeleteBaseRate/${id}`, { id }, { headers: authHeader() });
    await fetchBaseRates();
  };

  // ── Haulage categories ─────────────────────────────────────────────────────
  // API response shape: { id, haulageCategory, priceChangePCT, priceChangeAdj, comments }

  const fetchHaulageCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await customAxios.get(`/Ops/GetHaulageCategory`, { headers: authHeader() });
      setHaulageCategories(data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch haulage categories');
    } finally {
      setLoading(false);
    }
  }, []);

  // Update only — Add & Delete are disabled for now
  const updateHaulageCategory = async payload => {
    console.log('PAYLOAD ====', payload);
    // await customAxios.post(`Ops/UpdateHaulageCategory`, payload, { headers: authHeader() });
    await fetchHaulageCategories();
  };

  return {
    // rate card
    rateCards,
    loading,
    error,
    calculate,
    // base rates
    baseRates,
    fetchBaseRates,
    addBaseRate,
    updateBaseRate,
    deleteBaseRate,
    // haulage categories
    haulageCategories,
    fetchHaulageCategories,
    updateHaulageCategory,
    fetchParams,
    updateParams,
  };
};
